import React, { useState, useMemo } from 'react';
import { Calendar, Clock, AlertCircle, Sparkles, RefreshCw, AlertTriangle } from 'lucide-react';
import { generateDeadlines } from '../../utils/dataGeneration';
import { getCollegeName, getSchoolColor } from '../../shared/designSystem';
import SchoolCard from '../../shared/SchoolCard';

const TimelineTab = ({ colleges = [], currentUser, onDataGenerated, onDeleteCollege }) => {
  const [generatingForCollege, setGeneratingForCollege] = useState(null);

  // Generate deadlines for a college
  const handleGenerateDeadlines = async (college) => {
    setGeneratingForCollege(college.id);
    try {
      await generateDeadlines(college, () => {
        if (onDataGenerated) onDataGenerated();
      });
    } finally {
      setGeneratingForCollege(null);
    }
  };

  // Check if college has deadline data
  const hasDeadlineData = (college) => {
    const gc = college.global_colleges;
    return gc?.ed_deadline || gc?.ea_deadline || gc?.ed2_deadline || gc?.rd_deadline;
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return null;
    try {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (e) {
      return null;
    }
  };

  // Get deadline based on application round
  const getEarlyDeadline = (college) => {
    const gc = college.global_colleges;
    if (!gc) return null;

    switch (college.application_round) {
      case 'early_decision':
        return formatDate(gc.ed_deadline);
      case 'early_action':
        return formatDate(gc.ea_deadline);
      case 'early_decision_2':
        return formatDate(gc.ed2_deadline);
      default:
        return null;
    }
  };

  // Get application round label
  const getRoundLabel = (round) => {
    const labels = {
      'early_decision': 'Early Decision',
      'early_action': 'Early Action',
      'early_decision_2': 'ED II',
      'regular_decision': 'Regular Decision',
      'rolling': 'Rolling Admission'
    };
    return labels[round] || 'Regular Decision';
  };

  // Get round badge color (updated to match design system)
  const getRoundColor = (round) => {
    switch (round) {
      case 'early_decision':
        return 'bg-white text-rose-700 border-2 border-slate-300';
      case 'early_action':
        return 'bg-white text-amber-700 border-2 border-slate-300';
      case 'rolling':
        return 'bg-white text-violet-700 border-2 border-slate-300';
      default:
        return 'bg-white text-slate-700 border-2 border-slate-300';
    }
  };

  // Get all deadlines for conflict detection
  const allDeadlines = useMemo(() => {
    const deadlines = [];
    colleges.forEach((college) => {
      const gc = college.global_colleges;
      const collegeName = getCollegeName(college);

      // Early deadline
      if (college.application_round === 'early_decision' && gc?.ed_deadline) {
        deadlines.push({
          college: collegeName,
          type: 'ED Application',
          date: new Date(gc.ed_deadline),
          dateString: formatDate(gc.ed_deadline)
        });
      } else if (college.application_round === 'early_action' && gc?.ea_deadline) {
        deadlines.push({
          college: collegeName,
          type: 'EA Application',
          date: new Date(gc.ea_deadline),
          dateString: formatDate(gc.ea_deadline)
        });
      }

      // RD deadline
      if (gc?.rd_deadline) {
        deadlines.push({
          college: collegeName,
          type: 'RD Application',
          date: new Date(gc.rd_deadline),
          dateString: formatDate(gc.rd_deadline)
        });
      }

      // Financial aid deadlines
      if (gc?.finaid_deadline) {
        deadlines.push({
          college: collegeName,
          type: 'Financial Aid',
          date: new Date(gc.finaid_deadline),
          dateString: formatDate(gc.finaid_deadline)
        });
      }
    });

    return deadlines.sort((a, b) => a.date - b.date);
  }, [colleges]);

  // Detect deadline conflicts (same day or within 3 days)
  const conflicts = useMemo(() => {
    const conflictPairs = [];
    for (let i = 0; i < allDeadlines.length - 1; i++) {
      for (let j = i + 1; j < allDeadlines.length; j++) {
        const daysDiff = Math.abs((allDeadlines[j].date - allDeadlines[i].date) / (1000 * 60 * 60 * 24));
        if (daysDiff <= 3) {
          conflictPairs.push({
            deadline1: allDeadlines[i],
            deadline2: allDeadlines[j],
            daysDiff: Math.round(daysDiff)
          });
        }
      }
    }
    return conflictPairs;
  }, [allDeadlines]);

  return (
    <div className="space-y-8">
      {/* Deadline Conflict Warnings */}
      {conflicts.length > 0 && (
        <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-base font-bold text-amber-900 mb-2">Deadline Conflicts Detected</h3>
              <p className="text-xs text-amber-800 mb-3">
                The following deadlines occur within 3 days of each other. Plan ahead to avoid last-minute stress:
              </p>
              <ul className="space-y-2">
                {conflicts.map((conflict, idx) => (
                  <li key={idx} className="text-xs text-amber-800">
                    <strong>{conflict.deadline1.college}</strong> ({conflict.deadline1.type}) on {conflict.deadline1.dateString}
                    {' → '}
                    <strong>{conflict.deadline2.college}</strong> ({conflict.deadline2.type}) on {conflict.deadline2.dateString}
                    <span className="text-xs ml-2 text-amber-700">
                      ({conflict.daysDiff === 0 ? 'Same day!' : `${conflict.daysDiff} day${conflict.daysDiff > 1 ? 's' : ''} apart`})
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Visual Timeline */}
      {allDeadlines.length > 0 && (
        <div>
          <h3 className="text-xl font-bold text-slate-900 mb-4">Upcoming Deadlines</h3>
          <div className="bg-white border-2 border-slate-300 rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {allDeadlines.slice(0, 8).map((deadline, idx) => (
                <div key={idx} className="border-2 border-slate-300 rounded-lg p-4 hover:border-slate-900 transition-all">
                  <div className="flex items-start justify-between mb-2">
                    <Clock className="w-4 h-4 text-slate-500 mt-0.5" />
                    <span className="text-xs font-semibold text-slate-500 uppercase">
                      {deadline.type}
                    </span>
                  </div>
                  <p className="font-bold text-slate-900 text-base mb-1">{deadline.college}</p>
                  <p className="text-xs text-slate-700 font-medium">{deadline.dateString}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Timeline Overview */}
      <div>
        <h3 className="text-xl font-bold text-slate-900 mb-4">Application Timeline & Deadlines</h3>

        <div className="overflow-x-auto bg-white border-2 border-slate-300 rounded-lg">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-slate-300 bg-slate-50">
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-900">School</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-900">Application Round</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-900">Deadline</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-900">Decision Date</th>
              </tr>
            </thead>
            <tbody>
              {colleges.map((college, idx) => {
                const round = college.application_round || 'regular_decision';
                const isEarly = round === 'early_decision' || round === 'early_action';

                return (
                  <React.Fragment key={college.id}>
                    {/* Early round if applicable */}
                    {isEarly && (
                      <tr className="border-b border-slate-200 bg-slate-50">
                        <td className="py-3 px-4">
                          <span className="font-medium text-slate-900">
                            {college.global_colleges?.name || college.name}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-3 py-1 text-xs font-semibold rounded-lg ${getRoundColor(round)}`}>
                            {getRoundLabel(round)}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-xs font-medium">
                          {getEarlyDeadline(college) || (
                            !hasDeadlineData(college) ? (
                              <button
                                onClick={() => handleGenerateDeadlines(college)}
                                disabled={generatingForCollege === college.id}
                                className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-teal-500 text-white font-medium rounded-lg border-2 border-slate-900 hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95"
                              >
                                {generatingForCollege === college.id ? (
                                  <>
                                    <RefreshCw className="w-3 h-3 animate-spin" />
                                    Generating...
                                  </>
                                ) : (
                                  <>
                                    <Sparkles className="w-3 h-3" />
                                    Generate
                                  </>
                                )}
                              </button>
                            ) : (
                              <span className="text-slate-500 italic text-xs">Check college website</span>
                            )
                          )}
                        </td>
                        <td className="py-3 px-4 text-xs text-slate-600">
                          {college.global_colleges?.decision_date || (
                            <span className="text-slate-500 italic text-xs">Mid-December</span>
                          )}
                        </td>
                      </tr>
                    )}

                    {/* Regular Decision */}
                    <tr className={`border-b border-slate-200 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                      <td className="py-3 px-4">
                        <span className={`font-medium text-slate-900 ${!isEarly ? '' : 'pl-4 text-slate-600'}`}>
                          {!isEarly && (college.global_colleges?.name || college.name)}
                          {isEarly && '↳ Regular Decision Option'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {!isEarly && (
                          <span className={`px-3 py-1 text-xs font-semibold rounded-lg ${getRoundColor('regular_decision')}`}>
                            Regular Decision
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-xs font-medium">
                        {formatDate(college.global_colleges?.rd_deadline) || (
                          !hasDeadlineData(college) ? (
                            <button
                              onClick={() => handleGenerateDeadlines(college)}
                              disabled={generatingForCollege === college.id}
                              className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-teal-500 text-white font-medium rounded-lg border-2 border-slate-900 hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95"
                            >
                              {generatingForCollege === college.id ? (
                                <>
                                  <RefreshCw className="w-3 h-3 animate-spin" />
                                  Generating...
                                </>
                              ) : (
                                <>
                                  <Sparkles className="w-3 h-3" />
                                  Generate
                                </>
                              )}
                            </button>
                          ) : (
                            <span className="text-slate-500 italic text-xs">Check college website</span>
                          )
                        )}
                      </td>
                      <td className="py-3 px-4 text-xs text-slate-600">
                        {college.global_colleges?.decision_date || (
                          <span className="text-slate-500 italic text-xs">Mid-March to early April</span>
                        )}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Financial Aid Deadlines */}
      <div>
        <h3 className="text-xl font-bold text-slate-900 mb-4">Financial Aid & Scholarship Deadlines</h3>
        <div className="overflow-x-auto bg-white border-2 border-slate-300 rounded-lg">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-slate-300 bg-slate-50">
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-900">School</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-900">Financial Aid</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-900">CSS Profile</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-900">Merit Scholarships</th>
              </tr>
            </thead>
            <tbody>
              {colleges.map((college, idx) => {
                const gc = college.global_colleges;
                return (
                  <tr key={`finaid-${college.id}`} className={`border-b border-slate-200 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                    <td className="py-3 px-4 font-medium text-slate-900">
                      {gc?.name || college.name}
                    </td>
                    <td className="py-3 px-4 text-xs">
                      {formatDate(gc?.finaid_deadline) || (
                        !hasDeadlineData(college) ? (
                          <button
                            onClick={() => handleGenerateDeadlines(college)}
                            disabled={generatingForCollege === college.id}
                            className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-teal-500 text-white font-medium rounded-lg border-2 border-slate-900 hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95"
                          >
                            {generatingForCollege === college.id ? (
                              <>
                                <RefreshCw className="w-3 h-3 animate-spin" />
                                Generating...
                              </>
                            ) : (
                              <>
                                <Sparkles className="w-3 h-3" />
                                Generate
                              </>
                            )}
                          </button>
                        ) : (
                          <span className="text-slate-500 italic text-xs">Check college website</span>
                        )
                      )}
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-600">
                      {formatDate(gc?.css_deadline) || (
                        <span className="text-slate-500 italic text-xs">If required</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-600">
                      {formatDate(gc?.merit_deadline) || formatDate(gc?.honors_deadline) || (
                        <span className="text-slate-500 italic text-xs">Varies</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Key Dates Summary */}
      <div className="bg-white rounded-lg border-2 border-slate-300 p-6">
        <div className="flex items-start gap-2 mb-4">
          <Calendar className="w-5 h-5 text-teal-500 mt-0.5" />
          <h3 className="text-xs font-bold text-slate-900">General Timeline Reminders</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="text-xs font-semibold text-slate-900 mb-2">Application Deadlines</h4>
            <ul className="text-xs text-slate-700 space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="text-teal-500 mt-0.5">•</span>
                <span>Early Decision/Action: Typically November 1</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal-500 mt-0.5">•</span>
                <span>ED II: Typically January 1</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal-500 mt-0.5">•</span>
                <span>Regular Decision: January 1-15 (varies by school)</span>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-slate-900 mb-2">Financial Aid</h4>
            <ul className="text-xs text-slate-700 space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="text-teal-500 mt-0.5">•</span>
                <span>FAFSA opens October 1</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal-500 mt-0.5">•</span>
                <span>CSS Profile typically due with application</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-teal-500 mt-0.5">•</span>
                <span>Check school-specific deadlines above</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Timeline Tips */}
      <div className="bg-slate-50 rounded-lg border-2 border-slate-300 p-4">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-xs font-bold text-slate-900 mb-2">Timeline Management Tips</h4>
            <ul className="text-xs text-slate-700 space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="text-slate-400 mt-0.5">•</span>
                <span>Submit applications at least 2 days before deadlines to avoid technical issues</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-slate-400 mt-0.5">•</span>
                <span>Request transcripts and recommendations 3-4 weeks in advance</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-slate-400 mt-0.5">•</span>
                <span>Complete CSS Profile in October for early applicants</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-slate-400 mt-0.5">•</span>
                <span>FAFSA opens October 1 - complete as soon as possible</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimelineTab;